import {
  CircleAlert,
  Coffee,
  Droplets,
  EllipsisVertical,
  type LucideIcon,
  Pencil,
  Smile,
  Tag,
  Trash2,
  Utensils,
  X,
  Zap,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ServerEntry {
  id: string
  kind: string
  occurred_at: string
  notes: string | null
  data: Record<string, unknown>
  tags: { id: number; name: string }[]
  same_day_count?: number
}

export interface EntryUpdatePayload {
  notes?: string | null
  data?: Record<string, unknown>
  tag_ids?: number[]
}

interface EntryDetailSheetProps {
  entry: ServerEntry | null
  open: boolean
  onClose: () => void
  onSave: (id: string, data: EntryUpdatePayload) => void
  onDelete: (id: string) => void
  availableTags: { id: number; name: string }[]
}

// ── Config ────────────────────────────────────────────────────────────────────

const entryTypeConfig: Record<
  string,
  { icon: LucideIcon; bgVar: string; iconVar: string; label: string }
> = {
  symptom: {
    icon: CircleAlert,
    bgVar: "--fig-entry-symptom-bg",
    iconVar: "--fig-entry-symptom-icon",
    label: "Symptom",
  },
  meal: {
    icon: Utensils,
    bgVar: "--fig-entry-meal-bg",
    iconVar: "--fig-entry-meal-icon",
    label: "Meal",
  },
  mood: {
    icon: Smile,
    bgVar: "--fig-entry-mood-bg",
    iconVar: "--fig-entry-mood-icon",
    label: "Mood",
  },
  energy: {
    icon: Zap,
    bgVar: "--fig-entry-mood-bg",
    iconVar: "--fig-entry-mood-icon",
    label: "Energy",
  },
  beverage: {
    icon: Coffee,
    bgVar: "--fig-entry-beverage-bg",
    iconVar: "--fig-entry-beverage-icon",
    label: "Drink",
  },
  drink: {
    icon: Coffee,
    bgVar: "--fig-entry-beverage-bg",
    iconVar: "--fig-entry-beverage-icon",
    label: "Drink",
  },
  water: {
    icon: Droplets,
    bgVar: "--fig-entry-water-bg",
    iconVar: "--fig-entry-water-icon",
    label: "Water",
  },
  stool: {
    icon: Droplets,
    bgVar: "--fig-entry-stool-bg",
    iconVar: "--fig-entry-stool-icon",
    label: "Stool",
  },
}

const SYMPTOM_TYPES = [
  "Bloating",
  "Cramping",
  "Nausea",
  "Heartburn",
  "Gas",
  "Diarrhea",
  "Constipation",
  "Acid reflux",
]

const moodOptions = [
  { level: 1, emoji: "😫", label: "Awful" },
  { level: 2, emoji: "😕", label: "Low" },
  { level: 3, emoji: "😐", label: "Okay" },
  { level: 4, emoji: "🙂", label: "Good" },
  { level: 5, emoji: "😊", label: "Great" },
]

const energyOptions = [
  { level: 1, emoji: "🪫", label: "Drained" },
  { level: 2, emoji: "😴", label: "Low" },
  { level: 3, emoji: "⚡", label: "Moderate" },
  { level: 4, emoji: "💪", label: "High" },
  { level: 5, emoji: "🔥", label: "Wired" },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const isToday =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
  return isToday ? `Today at ${time}` : time
}

function entryTitle(entry: ServerEntry): string {
  switch (entry.kind) {
    case "symptom": {
      const t = entry.data.symptom_type as string | undefined
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Symptom"
    }
    case "meal": {
      const t = entry.data.meal_type as string | undefined
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Meal"
    }
    case "drink": {
      const t = entry.data.drink_type as string | undefined
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Drink"
    }
    case "stool": {
      const bristolType = entry.data.bristol_type as number | undefined
      if (bristolType !== undefined) {
        return `Stool · Type ${bristolType}`
      }
      return "Stool"
    }
    case "mood":
      return "Mood check-in"
    case "energy":
      return "Energy check-in"
    default:
      return entry.kind.charAt(0).toUpperCase() + entry.kind.slice(1)
  }
}

function resolvedConfig(entry: ServerEntry) {
  if (entry.kind === "drink") {
    const dt = entry.data.drink_type as string | undefined
    if (dt === "water") return entryTypeConfig.water
    return entryTypeConfig.beverage
  }
  return entryTypeConfig[entry.kind] ?? entryTypeConfig.meal
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"]
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// ── Section Label ─────────────────────────────────────────────────────────────

function SectionLabel({
  icon: Icon,
  label,
}: {
  icon: LucideIcon
  label: string
}) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      <Icon size={14} style={{ color: "var(--fig-text-inactive)" }} />
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--fig-text-inactive)",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
    </div>
  )
}

// ── View Content (kind-adaptive) ──────────────────────────────────────────────

function ViewContent({ entry }: { entry: ServerEntry }) {
  const sections: React.ReactNode[] = []

  // Kind-specific data section
  if (entry.kind === "symptom") {
    const severity = entry.data.severity as number | undefined
    const symptomType = entry.data.symptom_type as string | undefined
    sections.push(
      <div key="symptom-data" className="mb-5">
        <SectionLabel icon={CircleAlert} label="Symptom Details" />
        <div className="flex flex-wrap gap-2">
          {symptomType && (
            <span
              className="rounded-lg px-3 py-1.5"
              style={{
                fontSize: 13,
                fontWeight: 500,
                background: "var(--fig-detail-section-bg)",
                color: "var(--fig-text-primary)",
              }}
            >
              {symptomType.charAt(0).toUpperCase() + symptomType.slice(1)}
            </span>
          )}
          {severity !== undefined && (
            <span
              className="rounded-lg px-3 py-1.5"
              style={{
                fontSize: 13,
                fontWeight: 500,
                background: "var(--fig-entry-symptom-bg)",
                color: "var(--fig-entry-symptom-icon)",
              }}
            >
              {severity}/5 severity
            </span>
          )}
        </div>
      </div>,
    )
  } else if (entry.kind === "stool") {
    const bristolType = entry.data.bristol_type as number | undefined
    const description = entry.data.description as string | undefined
    sections.push(
      <div key="stool-data" className="mb-5">
        <SectionLabel icon={Droplets} label="Bristol Type" />
        <span
          className="rounded-lg px-3 py-1.5"
          style={{
            fontSize: 13,
            fontWeight: 500,
            background: "var(--fig-detail-section-bg)",
            color: "var(--fig-text-primary)",
            display: "inline-block",
          }}
        >
          {bristolType !== undefined ? `Type ${bristolType}` : "—"}
          {description ? ` — ${description}` : ""}
        </span>
      </div>,
    )
  } else if (entry.kind === "mood" || entry.kind === "energy") {
    const level = entry.data.level as number | undefined
    const options = entry.kind === "mood" ? moodOptions : energyOptions
    const option = options.find((o) => o.level === level)
    const count = entry.same_day_count
    sections.push(
      <div key="checkin-data" className="mb-5">
        <SectionLabel
          icon={entry.kind === "mood" ? Smile : Zap}
          label={entry.kind === "mood" ? "Mood Level" : "Energy Level"}
        />
        <span
          className="rounded-lg px-3 py-1.5"
          style={{
            fontSize: 13,
            fontWeight: 500,
            background: "var(--fig-detail-section-bg)",
            color: "var(--fig-text-primary)",
            display: "inline-block",
          }}
        >
          {option
            ? `${option.emoji} ${option.label} (${level}/5)`
            : `${level}/5`}
        </span>
        {count && count > 1 && (
          <p
            style={{
              fontSize: 13,
              color: "var(--fig-text-inactive)",
              marginTop: 8,
            }}
          >
            {ordinal(count)} {entry.kind} check-in today
          </p>
        )}
      </div>,
    )
  } else if (entry.kind === "meal") {
    const mealType = entry.data.meal_type as string | undefined
    const ingredients = entry.data.ingredients as string[] | undefined

    if (mealType) {
      sections.push(
        <div key="meal-type" className="mb-5">
          <SectionLabel icon={Utensils} label="Meal Type" />
          <span
            className="rounded-lg px-3 py-1.5"
            style={{
              fontSize: 13,
              fontWeight: 500,
              background: "var(--fig-detail-section-bg)",
              color: "var(--fig-text-primary)",
              display: "inline-block",
            }}
          >
            {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
          </span>
        </div>,
      )
    }

    if (ingredients && ingredients.length > 0) {
      sections.push(
        <div key="meal-ingredients" className="mb-5">
          <SectionLabel icon={Utensils} label="Ingredients" />
          <div className="flex flex-wrap gap-2">
            {ingredients.map((name) => (
              <span
                key={name}
                className="rounded-lg px-3 py-1.5"
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  background: "var(--fig-detail-section-bg)",
                  color: "var(--fig-text-primary)",
                }}
              >
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </span>
            ))}
          </div>
        </div>,
      )
    }

    if (!mealType && (!ingredients || ingredients.length === 0)) {
      sections.push(
        <div key="meal-data" className="mb-5">
          <SectionLabel icon={Utensils} label="Meal" />
          <span
            className="rounded-lg px-3 py-1.5"
            style={{
              fontSize: 13,
              fontWeight: 500,
              background: "var(--fig-detail-section-bg)",
              color: "var(--fig-text-primary)",
              display: "inline-block",
            }}
          >
            Meal
          </span>
        </div>,
      )
    }
  } else if (entry.kind === "drink") {
    const drinkType = entry.data.drink_type as string | undefined
    const count = entry.same_day_count
    // For simple drink types (water, coffee, etc.) the title already shows the type,
    // so show same-day count context instead of redundant type label
    if (count && count > 1) {
      const kindLabel = drinkType === "water" ? "water" : (drinkType ?? "drink")
      sections.push(
        <div key="drink-data" className="mb-5">
          <span
            style={{
              fontSize: 13,
              color: "var(--fig-text-inactive)",
            }}
          >
            {ordinal(count)} {kindLabel} today
          </span>
        </div>,
      )
    }
    // If count is 1 or absent, and there's nothing else to show, skip the redundant type section
  }

  if (entry.tags && entry.tags.length > 0) {
    sections.push(
      <div key="tags" className="mb-5">
        <SectionLabel icon={Tag} label="Tags" />
        <div className="flex flex-wrap gap-2">
          {entry.tags.map((tag) => (
            <span
              key={tag.id}
              className="rounded-lg px-2.5 py-1"
              style={{
                fontSize: 12,
                fontWeight: 600,
                background: "var(--fig-tag-bg)",
                color: "var(--fig-tag-text)",
              }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      </div>,
    )
  }

  if (entry.notes) {
    sections.push(
      <div key="notes" className="mb-5">
        <SectionLabel icon={Pencil} label="Notes" />
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: "var(--fig-text-secondary)",
          }}
        >
          {entry.notes}
        </p>
      </div>,
    )
  }

  if (sections.length === 0) {
    return (
      <p
        style={{
          fontSize: 13,
          color: "var(--fig-text-placeholder)",
          paddingBottom: 8,
        }}
      >
        No additional details
      </p>
    )
  }

  return <>{sections}</>
}

// ── Edit Content ──────────────────────────────────────────────────────────────

interface EditState {
  notes: string
  tagIds: number[]
  // kind-specific
  severity: number | null
  symptomType: string | null
  bristolType: number | null
  level: number | null
}

function EditContent({
  entry,
  state,
  onChange,
  availableTags,
}: {
  entry: ServerEntry
  state: EditState
  onChange: (update: Partial<EditState>) => void
  availableTags: { id: number; name: string }[]
}) {
  const unselectedTags = availableTags.filter(
    (t) => !state.tagIds.includes(t.id),
  )

  return (
    <div>
      {/* Kind-specific editable fields */}
      {entry.kind === "symptom" && (
        <>
          <div className="mb-5">
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fig-text-inactive)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 10,
              }}
            >
              Type
            </p>
            <div
              role="radiogroup"
              aria-label="Symptom type"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {SYMPTOM_TYPES.map((type) => {
                const isSelected =
                  state.symptomType?.toLowerCase() === type.toLowerCase()
                return (
                  <button
                    key={type}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() =>
                      onChange({ symptomType: type.toLowerCase() })
                    }
                    style={{
                      border: isSelected
                        ? `2px solid var(--fig-entry-symptom-icon)`
                        : `2px solid var(--fig-card-border)`,
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected
                        ? "var(--fig-entry-symptom-icon)"
                        : "var(--fig-text-primary)",
                      background: isSelected
                        ? "var(--fig-entry-symptom-bg)"
                        : "var(--fig-action-item-hover)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {type}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="mb-5">
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fig-text-inactive)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 10,
              }}
            >
              Severity
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              {[1, 2, 3, 4, 5].map((level) => {
                const isSelected = state.severity === level
                return (
                  <button
                    key={level}
                    type="button"
                    aria-label={`Severity ${level}`}
                    aria-pressed={isSelected}
                    onClick={() => onChange({ severity: level })}
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      border: isSelected
                        ? `2px solid var(--fig-entry-symptom-icon)`
                        : `2px solid var(--fig-card-border)`,
                      background: isSelected
                        ? "var(--fig-entry-symptom-bg)"
                        : "var(--fig-action-item-hover)",
                      color: isSelected
                        ? "var(--fig-entry-symptom-icon)"
                        : "var(--fig-text-primary)",
                      fontSize: 16,
                      fontWeight: isSelected ? 700 : 400,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                    }}
                  >
                    {level}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {entry.kind === "stool" && (
        <div className="mb-5">
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--fig-text-inactive)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 10,
            }}
          >
            Bristol Type
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[1, 2, 3, 4, 5, 6, 7].map((t) => {
              const isSelected = state.bristolType === t
              return (
                <button
                  key={t}
                  type="button"
                  aria-label={`Bristol Type ${t}`}
                  aria-pressed={isSelected}
                  onClick={() => onChange({ bristolType: t })}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    border: isSelected
                      ? `2px solid var(--fig-entry-stool-icon)`
                      : `2px solid var(--fig-card-border)`,
                    background: isSelected
                      ? "var(--fig-entry-stool-bg)"
                      : "var(--fig-action-item-hover)",
                    color: isSelected
                      ? "var(--fig-entry-stool-icon)"
                      : "var(--fig-text-primary)",
                    fontSize: 15,
                    fontWeight: isSelected ? 700 : 400,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {t}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {(entry.kind === "mood" || entry.kind === "energy") && (
        <div className="mb-5">
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--fig-text-inactive)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 10,
            }}
          >
            {entry.kind === "mood" ? "Mood Level" : "Energy Level"}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            {(entry.kind === "mood" ? moodOptions : energyOptions).map(
              (opt) => {
                const isSelected = state.level === opt.level
                return (
                  <button
                    key={opt.level}
                    type="button"
                    aria-label={`Level ${opt.level}: ${opt.label}`}
                    aria-pressed={isSelected}
                    onClick={() => onChange({ level: opt.level })}
                    style={{
                      flex: 1,
                      padding: "8px 4px",
                      borderRadius: 10,
                      border: isSelected
                        ? `2px solid var(--fig-entry-mood-icon)`
                        : `2px solid var(--fig-card-border)`,
                      background: isSelected
                        ? "var(--fig-entry-mood-bg)"
                        : "var(--fig-action-item-hover)",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{opt.emoji}</span>
                    <span
                      style={{
                        fontSize: 10,
                        color: "var(--fig-text-inactive)",
                      }}
                    >
                      {opt.label}
                    </span>
                  </button>
                )
              },
            )}
          </div>
        </div>
      )}

      {/* Tags */}
      <div className="mb-5">
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--fig-text-inactive)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 10,
          }}
        >
          Tags
        </p>
        <div className="flex flex-wrap gap-2">
          {state.tagIds.map((id) => {
            const tag = availableTags.find((t) => t.id === id)
            if (!tag) return null
            return (
              <button
                key={id}
                type="button"
                onClick={() =>
                  onChange({ tagIds: state.tagIds.filter((t) => t !== id) })
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 8,
                  background: "var(--fig-tag-bg)",
                  color: "var(--fig-tag-text)",
                  fontSize: 12,
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {tag.name}
                <X size={10} aria-hidden="true" />
              </button>
            )
          })}
        </div>
        {unselectedTags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {unselectedTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => onChange({ tagIds: [...state.tagIds, tag.id] })}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 10px",
                  borderRadius: 8,
                  background: "var(--fig-action-item-hover)",
                  color: "var(--fig-text-inactive)",
                  fontSize: 12,
                  fontWeight: 500,
                  border: `1px dashed var(--fig-divider-color)`,
                  cursor: "pointer",
                }}
              >
                + {tag.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="mb-5">
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--fig-text-inactive)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 10,
          }}
        >
          Notes
        </p>
        <textarea
          value={state.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Add notes (optional)"
          rows={3}
          style={{
            width: "100%",
            background: "var(--fig-action-item-hover)",
            border: `1px solid var(--fig-card-border)`,
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 14,
            color: "var(--fig-text-primary)",
            resize: "vertical",
            outline: "none",
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export function EntryDetailSheet({
  entry,
  open,
  onClose,
  onSave,
  onDelete,
  availableTags,
}: EntryDetailSheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  const [editMode, setEditMode] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [editState, setEditState] = useState<EditState>({
    notes: "",
    tagIds: [],
    severity: null,
    symptomType: null,
    bristolType: null,
    level: null,
  })

  const buildEditState = useCallback(
    (e: ServerEntry): EditState => ({
      notes: e.notes ?? "",
      tagIds: e.tags.map((t) => t.id),
      severity:
        e.kind === "symptom"
          ? ((e.data.severity as number | undefined) ?? null)
          : null,
      symptomType:
        e.kind === "symptom"
          ? ((e.data.symptom_type as string | undefined) ?? null)
          : null,
      bristolType:
        e.kind === "stool"
          ? ((e.data.bristol_type as number | undefined) ?? null)
          : null,
      level:
        e.kind === "mood" || e.kind === "energy"
          ? ((e.data.level as number | undefined) ?? null)
          : null,
    }),
    [],
  )

  const isDirty = useCallback((): boolean => {
    if (!entry) return false
    const initial = buildEditState(entry)
    return (
      editState.notes !== initial.notes ||
      JSON.stringify([...editState.tagIds].sort()) !==
        JSON.stringify([...initial.tagIds].sort()) ||
      editState.severity !== initial.severity ||
      editState.symptomType !== initial.symptomType ||
      editState.bristolType !== initial.bristolType ||
      editState.level !== initial.level
    )
  }, [entry, editState, buildEditState])

  const handleEditOpen = useCallback(() => {
    if (entry) setEditState(buildEditState(entry))
    setEditMode(true)
  }, [entry, buildEditState])

  const doClose = useCallback(() => {
    setEditMode(false)
    setShowDiscardDialog(false)
    onClose()
  }, [onClose])

  const handleClose = useCallback(() => {
    if (editMode && isDirty()) {
      setShowDiscardDialog(true)
      return
    }
    doClose()
  }, [editMode, isDirty, doClose])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) handleClose()
    },
    [handleClose],
  )

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, handleClose])

  useEffect(() => {
    if (open) closeRef.current?.focus()
  }, [open])

  const handleEditChange = useCallback((update: Partial<EditState>) => {
    setEditState((prev) => ({ ...prev, ...update }))
  }, [])

  const handleSave = useCallback(() => {
    if (!entry) return
    const payload: EntryUpdatePayload = {
      notes: editState.notes.trim() || null,
      tag_ids: editState.tagIds,
    }
    // Merge kind-specific data
    const dataUpdate: Record<string, unknown> = { ...entry.data }
    if (entry.kind === "symptom") {
      if (editState.symptomType !== null)
        dataUpdate.symptom_type = editState.symptomType
      if (editState.severity !== null) dataUpdate.severity = editState.severity
    } else if (entry.kind === "stool") {
      if (editState.bristolType !== null)
        dataUpdate.bristol_type = editState.bristolType
    } else if (entry.kind === "mood" || entry.kind === "energy") {
      if (editState.level !== null) dataUpdate.level = editState.level
    }
    payload.data = dataUpdate
    onSave(entry.id, payload)
    setEditMode(false)
  }, [entry, editState, onSave])

  if (!entry) return null

  const config = resolvedConfig(entry)
  const Icon = config.icon
  const title = entryTitle(entry)
  const timeLabel = formatTime(entry.occurred_at)

  return (
    <div
      ref={overlayRef}
      className={`entry-detail-overlay fixed inset-0 z-50 flex items-end justify-center ${open ? "open" : ""}`}
      style={{ background: "var(--fig-overlay-bg)" }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="entry-detail-title"
    >
      <div
        className="entry-detail-panel flex w-full max-w-md flex-col"
        style={{
          maxHeight: "80dvh",
          background: "var(--fig-modal-bg)",
          borderRadius: "20px 20px 0 0",
          boxShadow: "var(--fig-modal-shadow)",
        }}
      >
        {/* Handle */}
        <div className="shrink-0 px-5 pt-3">
          <div className="mb-3 flex justify-center">
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "var(--fig-modal-handle)",
              }}
            />
          </div>

          {/* Header */}
          <div className="mb-4 flex items-center gap-3">
            <div
              className="flex shrink-0 items-center justify-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: `var(${config.bgVar})`,
              }}
            >
              <Icon size={24} style={{ color: `var(${config.iconVar})` }} />
            </div>
            <div className="min-w-0 flex-1">
              <h2
                id="entry-detail-title"
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </h2>
              <span style={{ fontSize: 13, color: "var(--fig-text-inactive)" }}>
                {timeLabel}
              </span>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={handleClose}
              aria-label="Close"
              style={{
                width: 30,
                height: 30,
                borderRadius: 15,
                background: "var(--fig-tag-bg)",
                border: "none",
                cursor: "pointer",
                color: "var(--fig-text-inactive)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: "var(--fig-divider-color)",
            opacity: 0.5,
          }}
        />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {editMode ? (
            <EditContent
              entry={entry}
              state={editState}
              onChange={handleEditChange}
              availableTags={availableTags}
            />
          ) : (
            <ViewContent entry={entry} />
          )}
        </div>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: "var(--fig-divider-color)",
            opacity: 0.5,
          }}
        />

        {/* Actions */}
        <div
          className="shrink-0 px-5 py-3"
          style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
        >
          {editMode ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditMode(false)}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "10px 0",
                  borderRadius: 12,
                  background: "var(--fig-tag-bg)",
                  color: "var(--fig-text-secondary)",
                  fontSize: 14,
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                style={{
                  flex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "10px 0",
                  borderRadius: 12,
                  background: "var(--fig-fab-bg)",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleEditOpen}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "10px 0",
                  borderRadius: 12,
                  background: "var(--fig-fab-bg)",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Pencil size={15} aria-hidden="true" />
                Edit
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="More options"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: "var(--fig-tag-bg)",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--fig-text-secondary)",
                      flexShrink: 0,
                    }}
                  >
                    <EllipsisVertical size={18} aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => onDelete(entry.id)}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Discard changes dialog */}
      <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Discard changes?</DialogTitle>
            <DialogDescription>
              You have unsaved changes that will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setShowDiscardDialog(false)}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                background: "var(--fig-tag-bg)",
                color: "var(--fig-text-secondary)",
                fontSize: 14,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={doClose}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                background: "var(--fig-destructive)",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              Discard
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
